/**
 * The shared report counting rules, in JavaScript (REQ-COUNT-1..7).
 *
 * The third implementation of one definition. `wellness_metrics.py` renders the
 * emailed report and `ReportMetrics.cs` serves the screen its recipient opens
 * from it; this one builds the in-dashboard report page. All three run the same
 * `req-count-golden.json`, byte for byte, so a divergence turns a test red in
 * whichever repo moved rather than turning an emailed number into a
 * contradiction of the screen.
 *
 * WHY NOT REUSE `unitEventsUtils.countAlertTransitions`: it counts a FOURTH
 * type, `isHomeTempOutOfRange`, which REQ-COUNT-1 excludes. That is deliberate
 * on the dashboard's side, where the alert charts and the 28-day safety trend
 * want the wider set, and it is wrong here, where the number has to equal the
 * one in the email. Two different questions, two different counts, and the
 * report page has to answer the email's. Anything that needs the dashboard's
 * wider reading should keep calling `unitEventsUtils`.
 *
 * Everything here is pure and React-free.
 */

/**
 * REQ-COUNT-1. Not the full alarm vocabulary: `isStoveShutoff` is counted
 * separately and the informational flags are not alerts at all.
 */
export const DANGER_TYPES = [
  "isTooHot",
  "isUnattended",
  "isImmediateDanger",
] as const;

export const SHUTOFF_TYPE = "isStoveShutoff";

export type AlarmRow = {
  datetime?: string;
  Datetime?: string;
  currentAlarms?: string[];
  currAlarms?: string[];
  prevAlarms?: string[];
};

export type SessionRow = {
  startDatetime?: string;
  dateStart?: string;
};

export type PresenceRow = {
  datetime?: string;
  Datetime?: string;
  detected?: boolean;
};

/**
 * Epoch millis, or null when there is nothing usable.
 *
 * A timestamp with no offset is UTC, matching how the sort keys are stored and
 * what the Python and C# sides do. `Date.parse` would read a naive string as
 * LOCAL time, which on a browser in Chicago shifts every count by five hours
 * and silently moves events across the window boundary. The golden fixture has
 * a case for exactly this.
 *
 * A malformed value is skipped rather than throwing: one bad row must not cost
 * a reader the whole report (REQ-COUNT-7).
 */
export function parseTimestamp(raw: unknown): number | null {
  if (typeof raw !== "string") return null;

  const text = raw.trim().replace(" ", "T");
  if (text.length === 0) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const ms = Date.parse(hasZone ? text : `${text}Z`);

  return Number.isNaN(ms) ? null : ms;
}

function inWindow(at: number | null, start: number, end: number): boolean {
  // Both bounds inclusive, matching the telemetry range queries the other two
  // implementations issue.
  return at !== null && at >= start && at <= end;
}

function currentOf(alarm: AlarmRow): string[] {
  return alarm.currentAlarms || alarm.currAlarms || [];
}

/**
 * Danger alerts and safety shut-offs raised in the window.
 *
 * REQ-COUNT-2: the rising edge only, present in `currentAlarms` and absent from
 * `prevAlarms`. Telemetry repeats the active set every frame, so counting
 * membership would report one long unattended alert as dozens.
 *
 * REQ-COUNT-3: a shut-off counts only when a danger type is live in the same
 * record, which is the "an alert went unanswered" case. A scheduled lock or a
 * manual shut-off from the app is not that.
 *
 * REQ-COUNT-4: the two are returned separately and never folded together.
 */
export function countAlertTransitions(
  alarms: AlarmRow[] | null | undefined,
  start: number,
  end: number
): { danger: number; shutoffs: number } {
  let danger = 0;
  let shutoffs = 0;

  for (const alarm of alarms || []) {
    if (!alarm) continue;

    const at = parseTimestamp(alarm.datetime ?? alarm.Datetime);
    if (!inWindow(at, start, end)) continue;

    const current = currentOf(alarm);
    const previous = alarm.prevAlarms || [];
    const raised = current.filter((type) => !previous.includes(type));

    danger += raised.filter(
      (type) => (DANGER_TYPES as readonly string[]).includes(type)
    ).length;

    if (
      raised.includes(SHUTOFF_TYPE) &&
      current.some((type) => (DANGER_TYPES as readonly string[]).includes(type))
    ) {
      shutoffs += 1;
    }
  }

  return { danger, shutoffs };
}

/**
 * REQ-COUNT-5. Sessions count by START time, so a session belongs to the period
 * it began in and an in-progress session still counts.
 */
export function countSessions(
  sessions: SessionRow[] | null | undefined,
  start: number,
  end: number
): number {
  let count = 0;

  for (const session of sessions || []) {
    if (!session) continue;
    const at = parseTimestamp(session.startDatetime ?? session.dateStart);
    if (inWindow(at, start, end)) count += 1;
  }

  return count;
}

/**
 * REQ-COUNT-6. The latest DETECTED row.
 *
 * A cleared row is not evidence of presence, and no evidence at all reports as
 * unknown, never as absence. An older row missing the field is treated as
 * detected, which is how those rows were written before the flag existed.
 */
export function latestPresence(
  presence: PresenceRow[] | null | undefined
): number | null {
  let latest: number | null = null;

  for (const row of presence || []) {
    if (!row) continue;
    if (row.detected === false) continue;

    const at = parseTimestamp(row.datetime ?? row.Datetime);
    if (at === null) continue;
    if (latest === null || at > latest) latest = at;
  }

  return latest;
}
